import React, { useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Moment from 'moment';
import { Box, PagePanel } from 'edenred-ui';

import { OrderStatus, ProductName } from '@epi-shared/components';
import { goToPage } from '@epi-actions/navigation';
import { IOrderDetails } from '@epi-models/containers/OrderDetails';

import { formatCurrency } from '../../helpers/numeral';
import { addressLine } from '../../helpers/Formatters';
import {
  isError as isErrorSelector,
  voucherOrder as voucherOrderSelector
} from '../../selectors/orderDetails';
import { momentDateFormat } from '../../constants/dateFormats';
import { DeliveryMethods } from '../../constants/deliveryMethods';
import { routerPaths } from '../../constants/routerPaths';
import * as apiActions from '../../actions/api';
import * as navigationActions from '../../actions/navigation';
import { NotFound } from '../NotFound/NotFound';
import './OrderDetails.scss';
import { NavigationHeader } from '../../components/NavigationHeader/NavigationHeader';
import { DownloadInvoiceButton } from '../../components/DownloadInvoiceButton/DownloadInvoiceButton';
import { LoadingContainer } from '../../components/LoadingContainer/LoadingContainer';

const OrderDetails = ({
  loadVoucherOrderDetails,
  voucherOrder,
  isError
}: IOrderDetails) => {
  const { voucherOrderId } = useParams<{ voucherOrderId: string }>();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const deliveryAddressToPickupPoint =
    voucherOrder &&
    voucherOrder.delivery.method === DeliveryMethods.ToPickupPoint &&
    t('containers.order_details.to_pickup_point');

  const deliveryAddressString =
    voucherOrder && voucherOrder.delivery.address
      ? `${addressLine(voucherOrder.delivery.address)}`
      : deliveryAddressToPickupPoint;

  useEffect(() => {
    loadVoucherOrderDetails(voucherOrderId);
  }, []);

  if (isError) {
    return <NotFound />;
  }

  return (
    <NavigationHeader
      withBackButton
      onClickBack={() => dispatch(goToPage(routerPaths.orderHistory))}
      title={t('containers.order_details.title')}
    >
      <div className="OrderDetails">
        <LoadingContainer isLoading={!voucherOrder}>
          {voucherOrder && (
            <PagePanel>
              <DownloadInvoiceButton
                idType="order"
                id={voucherOrderId}
                className="btn-download"
              >
                <span>{t('containers.order_details.download')}</span>
              </DownloadInvoiceButton>
              <Box display="flex" gap={20} mt={5}>
                <Box>
                  <Box mb={2}>
                    {t('containers.order_details.date')}
                    <h3 className="small">
                      {Moment(voucherOrder.date).format(momentDateFormat)}
                    </h3>
                  </Box>
                  <Box mb={2}>
                    {t('containers.order_details.orderer')}
                    <h3 className="small">{voucherOrder.orderer}</h3>
                  </Box>
                  <Box mb={2}>
                    {t('containers.order_details.order_number')}
                    <h3 className="small">
                      {voucherOrder.orderNumber ||
                        t('containers.order_details.pending')}
                    </h3>
                  </Box>
                  <Box mb={2}>
                    {t('containers.order_details.delivery_address')}
                    <h3 className="small">{deliveryAddressString || '-'}</h3>
                  </Box>
                  <Box mb={2}>
                    {t('containers.order_details.cost_center')}
                    <h3 className="small">
                      {voucherOrder.invoiceCostCenter || '-'}
                    </h3>
                  </Box>
                  <Box mb={2}>
                    {t('containers.order_details.product')}
                    <h3 className="small">
                      <ProductName product={voucherOrder.product} />{' '}
                      {formatCurrency(voucherOrder.faceValue)}
                    </h3>
                  </Box>
                </Box>
                <Box>
                  <Box mb={2}>
                    {t('containers.order_details.amount')}
                    <h3 className="small">
                      {voucherOrder.totalQuantity || '-'}
                    </h3>
                  </Box>
                  <Box mb={2}>
                    {t('containers.order_details.order_summary')}
                    <h3 className="small">
                      {t('containers.order_details.order_summary_value', {
                        faceValue: formatCurrency(voucherOrder.faceValue),
                        quantity: voucherOrder.totalQuantity,
                        total: formatCurrency(voucherOrder.totalAmount)
                      })}
                    </h3>
                  </Box>
                  <Box mb={2}>
                    {t('containers.order_details.handling_fee')}
                    <h3 className="small">
                      {formatCurrency(voucherOrder.handlingFee)}
                    </h3>
                  </Box>
                  {voucherOrder.creditCardFee > 0 && (
                    <Box mb={2}>
                      {t('containers.order_details.credit_card_fee')}
                      <h3 className="small">
                        {formatCurrency(voucherOrder.creditCardFee)}
                      </h3>
                    </Box>
                  )}
                  <Box mb={2}>
                    {t('containers.order_details.total_without_vat')}
                    <h3 className="small">
                      {formatCurrency(
                        voucherOrder.totalAmount + voucherOrder.handlingFee
                      )}
                    </h3>
                  </Box>
                  <Box mb={2}>
                    {t('containers.order_details.total_vat', {
                      vatFeePercent: voucherOrder.vatFeePercent
                    })}
                    <h3 className="small">
                      {formatCurrency(voucherOrder.vatFeeAmount)}
                    </h3>
                  </Box>
                  <Box mb={2}>
                    {t('containers.order_details.total')}
                    <h3 className="small">
                      {formatCurrency(voucherOrder.totalAmountWithFees)}
                    </h3>
                  </Box>
                </Box>
                <Box>
                  <Box mb={2}>
                    {t('containers.order_details.order_status')}
                    <h3 className="small">
                      <OrderStatus
                        paymentStatus={voucherOrder.paymentStatus}
                        voucherOrderStatus={voucherOrder.voucherOrderStatus}
                      />
                    </h3>
                  </Box>
                  <Box mb={2}>
                    {t('containers.order_details.tracking_code')}
                    <h3 className="small">
                      {voucherOrder.trackingCode ||
                        t('containers.order_details.pending')}
                    </h3>
                  </Box>
                  <Box mb={2}>
                    {t('containers.order_details.serial_numbers_of_vouchers')}
                    <h3 className="small">
                      {!voucherOrder.firstVoucherSerial &&
                      !voucherOrder.lastVoucherSerial
                        ? t('containers.order_details.pending')
                        : `${voucherOrder.firstVoucherSerial || ''}-${
                            voucherOrder.lastVoucherSerial || ''
                          }`}
                    </h3>
                  </Box>
                  <Box mb={2}>
                    {t('containers.order_details.vouchers_valid_until')}
                    <h3 className="small">
                      {Moment(voucherOrder.vouchersValidUntil).format(
                        momentDateFormat
                      )}
                    </h3>
                  </Box>
                </Box>
              </Box>
            </PagePanel>
          )}
        </LoadingContainer>
      </div>
    </NavigationHeader>
  );
};

const mapStateToProps = state => ({
  voucherOrder: voucherOrderSelector(state),
  isError: isErrorSelector(state)
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...apiActions, ...navigationActions }, dispatch);

const ConnectedOrderDetails = connect(
  mapStateToProps,
  mapDispatchToProps
)(OrderDetails);

export { ConnectedOrderDetails as OrderDetails };
